import enLocale from "@fullcalendar/core/locales/en-gb";
import svLocale from "@fullcalendar/core/locales/sv";
import interactionPlugin from "@fullcalendar/interaction";
import momentPlugin from "@fullcalendar/moment";
import FullCalendar from "@fullcalendar/react";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import timeGridPlugin from "@fullcalendar/timegrid";

import { Icon } from "semantic-ui-react";

import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import getArticleChannelData from "astrid-firestore/src/api/articles/getArticleChannelData";

import Button from "../../../ui/components/Buttons/Button";
import DropdownMenu from "../../../ui/components/DropdownMenu/DropdownMenu";
import themeColors from "../../../ui/constants/themeColors";

export default function ArticlesCalendarView({ organization, data, distributionChannels }) {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const ref = useRef();

	const organizationChannelIds = Object.values(organization?.distribution?.presets || {}).map(
		(preset) => preset.channel.id,
	);

	const filteredDistributionChannels = !organization
		? distributionChannels
		: distributionChannels.filter((channel) => organizationChannelIds.includes(channel.id));

	const defaultChannelId =
		filteredDistributionChannels.find(({ name }) => name === "Storytel")?.id || filteredDistributionChannels[0]?.id;

	const [channelId, setChannelId] = useState(defaultChannelId);
	const [startDate, setStartDate] = useState();
	const [endDate, setEndDate] = useState();

	if (!data) {
		return;
	}

	const resourcesDropdownOptions = filteredDistributionChannels
		.map((channel) => ({
			key: channel.id,
			value: channel.id,
			text: channel.name,
		}))
		.sort((a, b) => a.text.localeCompare(b.text));

	const resources = data.map(({ id, name, ...article }) => ({
		id,
		title: name,
		...article,
	}));

	const events = resources
		.filter((article) => article.channelIds.includes(channelId))
		.flatMap((article) => {
			const resourceId = article.id;

			const announcementDate = getArticleChannelData(article, channelId, "announcementDate");
			const releaseDate = getArticleChannelData(article, channelId, "releaseDate");
			const takedownDate = getArticleChannelData(article, channelId, "takedownDate");

			const titleAnnounced = `${t("announced", "Announced")} ${new Date(announcementDate).toLocaleDateString(
				undefined,
				{ month: "numeric", day: "numeric" },
			)}`;

			const titleReleased = `${t("published", "Published")} ${new Date(releaseDate).toLocaleDateString(
				undefined,
				{
					month: "numeric",
					day: "numeric",
				},
			)}`;

			const titleTakedown = `${t("unpublished", "Unpublished")} ${new Date(takedownDate).toLocaleDateString(
				undefined,
				{ month: "numeric", day: "numeric" },
			)}`;

			return [
				{
					resourceId,
					title: titleAnnounced,
					start: announcementDate ? new Date(announcementDate) : null,
					end: releaseDate ? new Date(new Date(releaseDate).getTime() - 10000000) : null,
					backgroundColor: themeColors.BLUE_FADED,
					borderColor: themeColors.BLUE_FADED,
					textColor: themeColors.BLUE,
				},
				{
					resourceId,
					title: titleReleased,
					start: releaseDate ? new Date(releaseDate) : null,
					end: takedownDate ? new Date(new Date(takedownDate).getTime() - 10000000) : endDate,
					backgroundColor: themeColors.GREEN_FADED,
					borderColor: themeColors.GREEN_FADED,
					textColor: themeColors.GREEN,
				},
				{
					resourceId,
					title: titleTakedown,
					start: takedownDate ? new Date(takedownDate) : null,
					end: endDate,
					backgroundColor: themeColors.RED_FADED,
					borderColor: themeColors.RED_FADED,
					textColor: themeColors.RED,
				},
			];
		});

	return (
		<div style={{ marginTop: 13 }}>
			<FullCalendar
				ref={ref}
				schedulerLicenseKey="0520726615-fcs-1698239718"
				plugins={[momentPlugin, resourceTimelinePlugin, interactionPlugin, timeGridPlugin]}
				initialView={"resourceTimelineYear"}
				height="70vh"
				locales={[enLocale, svLocale]}
				locale={i18n.language}
				firstDay={1}
				resources={resources}
				resourceOrder="name"
				resourceAreaWidth="20%"
				resourceAreaHeaderContent={
					<>
						<DropdownMenu
							options={resourcesDropdownOptions}
							onChange={(e, { value }) => setChannelId(value)}
							trigger={
								<Button basic circular size="small">
									{resourcesDropdownOptions.find(({ value }) => value === channelId)?.text}

									<Icon size="small" name="chevron down" fitted style={{ paddingLeft: 5 }} />
								</Button>
							}
							maxHeight="220px"
						/>
					</>
				}
				events={events}
				eventClick={({ event }) => {
					const resource = event.getResources()[0];

					navigate(`../${resource.id}/distributions`);
				}}
				eventOverlap={false}
				displayEventTime={false}
				slotLabelFormat={[
					"MMMM",
					{
						day: "numeric",
					},
				]}
				slotLabelContent={({ text }) => <div style={{ color: themeColors.TEXT }}>{text}</div>}
				datesSet={({ start, end }) => {
					if (startDate?.getTime() !== start.getTime()) {
						setStartDate(start);
					}
					if (endDate?.getTime() !== end.getTime()) {
						setEndDate(end);
					}
				}}
				scrollTime={{ months: new Date().getMonth(), day: new Date().getDate() - 1 }}
				scrollTimeReset={false}
				headerToolbar={false}
			/>
		</div>
	);
}
